import React, { useState, useEffect } from 'react';

/**
 * CookieConsent Wrapper
 * 
 * Checks if localStorage (cookies) is available before rendering the app.
 * - If localStorage is disabled → blocks with an error screen.
 * - If first visit → shows a consent banner (Accept / Decline).
 * - If declined → blocks with an error screen.
 * - If previously accepted → renders children normally.
 */

const CONSENT_KEY = 'cookiesAccepted';

/**
 * Attempt to write/read/delete from localStorage.
 * Returns true if localStorage is functional.
 */
function isLocalStorageAvailable() {
  try {
    const testKey = '__cookie_test__';
    localStorage.setItem(testKey, '1');
    const val = localStorage.getItem(testKey);
    localStorage.removeItem(testKey);
    return val === '1';
  } catch {
    return false;
  }
}

const CookieConsent = ({ children }) => {
  const [storageAvailable] = useState(() => isLocalStorageAvailable());
  const [consentState, setConsentState] = useState('pending'); // 'pending' | 'accepted' | 'declined'

  useEffect(() => {
    if (!storageAvailable) {
      setConsentState('declined');
      return;
    }
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === 'true') {
      setConsentState('accepted');
    }
    // Otherwise stays 'pending' → show banner
  }, [storageAvailable]);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'true');
    setConsentState('accepted');
  };

  const handleDecline = () => {
    setConsentState('declined');
  };

  // --- Blocked Screen (cookies disabled or declined) ---
  if (consentState === 'declined') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
        <div className="max-w-md w-full text-center bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700">
          {/* Warning Icon */}
          <div className="mx-auto mb-6 w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
            <svg className="w-9 h-9 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-white mb-3">
            Cookies Required
          </h1>
          <p className="text-gray-300 mb-6 text-sm leading-relaxed">
            {!storageAvailable
              ? 'Your browser has cookies and local storage disabled. The Order Management System requires these to function properly (for login sessions, preferences, etc.).'
              : 'You have declined cookies. The Order Management System cannot function without cookies enabled, as they are essential for authentication and session management.'
            }
          </p>

          <div className="bg-gray-700/50 rounded-lg p-4 mb-6 text-left">
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wide mb-2">How to enable cookies:</p>
            <ol className="text-gray-300 text-sm space-y-1 list-decimal list-inside">
              <li>Open your browser settings</li>
              <li>Navigate to Privacy & Security</li>
              <li>Enable cookies / site data</li>
              <li>Reload this page</li>
            </ol>
          </div>

          <button
            onClick={() => window.location.reload()}
            className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition duration-200 transform hover:scale-[1.01]"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  // --- Consent Banner (first visit) ---
  if (consentState === 'pending') {
    return (
      <>
        {/* Render app behind the banner but blurred */}
        <div className="filter blur-sm pointer-events-none">
          {children}
        </div>

        {/* Consent Banner Overlay */}
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-2xl mx-4 mb-6 bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 p-6 animate-slide-up">
            <div className="flex items-start gap-4">
              {/* Cookie Icon */}
              <div className="flex-shrink-0 w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center mt-1">
                <span className="text-xl">🍪</span>
              </div>

              <div className="flex-1">
                <h2 className="text-lg font-semibold text-white mb-1">
                  Cookie Consent
                </h2>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  This application uses cookies and local storage to manage your login session, 
                  preferences, and security features. These are essential for the system to function 
                  and are not used for tracking or advertising.
                </p>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleAccept}
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition duration-200"
                  >
                    Accept & Continue
                  </button>
                  <button
                    onClick={handleDecline}
                    className="px-5 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm font-medium rounded-lg transition duration-200"
                  >
                    Decline
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // --- Accepted → render app normally ---
  return children;
};

export default CookieConsent;
